import React, { useState, useMemo } from 'react';
import { useQueryClient, useQueries } from '@tanstack/react-query';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useRegisterSocketRooms } from '../hooks/useSocketInvalidation';
import { useMyVotingBoxes } from '../hooks/queries';
import { fetchVotingBoxes, fetchCampaigns } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Vote,
  Users,
  CheckCircle2,
  Edit2,
  List,
  UserCheck,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VotingBoxesPage() {
  const queryClient = useQueryClient();
  const { user: authUser } = useAuth();
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showVotersDialog, setShowVotersDialog] = useState(false);
  const [selectedBox, setSelectedBox] = useState(null);
  const [checkInId, setCheckInId] = useState('');
  const [checkInResult, setCheckInResult] = useState(null);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [editBoxData, setEditBoxData] = useState({
    manual_registered: '',
    manual_voted: '',
    manual_pledges_registered: '',
    manual_pledgers_voted: '',
  });
  const [boxVoters, setBoxVoters] = useState([]);
  const [boxVotersLoading, setBoxVotersLoading] = useState(false);
  const [boxVotersTotal, setBoxVotersTotal] = useState(0);
  const [boxVotersPage, setBoxVotersPage] = useState(1);
  const [boxVotersSearch, setBoxVotersSearch] = useState('');
  const boxVotersPageSize = 20;

  const { data: myBoxes = [], isLoading: myBoxesLoading, refetch: refetchMyBoxes } = useMyVotingBoxes();

  const campaignIds = useMemo(() => [...new Set((myBoxes || []).map((b) => b.campaign_id))], [myBoxes]);
  const constituencyByCampaign = useMemo(() => {
    const m = {};
    (myBoxes || []).forEach((b) => {
      m[b.campaign_id] = b.constituency_id;
    });
    return m;
  }, [myBoxes]);

  useRegisterSocketRooms(
    campaignIds.length > 0 ? campaignIds.flatMap((cid) => [`voting:${cid}`, `turnout:${cid}`]) : []
  );

  const boxQueries = useQueries({
    queries: campaignIds.map((cid) => ({
      queryKey: queryKeys.votingBoxes(cid),
      queryFn: () => fetchVotingBoxes(cid),
      enabled: !!cid,
    })),
  });

  const constituencyIds = useMemo(() => [...new Set(Object.values(constituencyByCampaign))], [constituencyByCampaign]);
  const campaignQueries = useQueries({
    queries: constituencyIds.map((constituencyId) => ({
      queryKey: queryKeys.campaigns(constituencyId),
      queryFn: () => fetchCampaigns(constituencyId),
      enabled: !!constituencyId,
    })),
  });

  const boxesByCampaign = useMemo(() => {
    const result = {};
    boxQueries.forEach((q, idx) => {
      const campaignId = campaignIds[idx];
      if (campaignId && q.data) {
        result[campaignId] = q.data;
      }
    });
    return result;
  }, [boxQueries, campaignIds]);

  const campaignNames = useMemo(() => {
    const names = {};
    campaignQueries.forEach((q, idx) => {
      const constituencyId = constituencyIds[idx];
      if (!constituencyId || !q.data) return;
      q.data.forEach((c) => {
        names[c.campaign_id] = c.name;
      });
    });
    return names;
  }, [campaignQueries, constituencyIds]);

  const isLoading = myBoxesLoading || boxQueries.some((q) => q.isLoading);

  const invalidateVoting = () => {
    queryClient.invalidateQueries({ queryKey: ['voting'] });
    queryClient.invalidateQueries({ queryKey: ['records'] });
    refetchMyBoxes();
    campaignIds.forEach((cid) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.votingBoxes(cid) });
    });
  };

  const handleCheckIn = async () => {
    const idToUse = checkInId.trim().toUpperCase();
    if (!idToUse || !selectedBox) {
      toast.error('Please enter an ID card number');
      return;
    }

    setCheckInLoading(true);
    setCheckInResult(null);

    try {
      const response = await api.post(
        `${API}/voting-boxes/${selectedBox.box_id}/check-in`,
        { id_number: idToUse }
      );
      setCheckInResult({
        success: true,
        message: 'Voter checked in successfully!',
        constituent: response.data.constituent,
      });
      toast.success(`${response.data.constituent.name} checked in!`);
      setCheckInId('');
      invalidateVoting();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Check-in failed';
      if (typeof detail === 'object' && detail !== null) {
        message = detail.message || message;
        if (detail.code === 'wrong_box' && detail.correct_box) {
          message = `This voter is not for this box. Correct box: ${detail.correct_box}`;
        } else if (detail.code === 'not_found') {
          message = 'ID card number not found in this campaign';
        } else if (detail.code === 'already_voted') {
          message = detail.message;
        }
      } else if (typeof detail === 'string') {
        message = detail;
      }
      setCheckInResult({ success: false, message });
    } finally {
      setCheckInLoading(false);
    }
  };

  const openCheckInDialog = (box) => {
    setSelectedBox(box);
    setCheckInId('');
    setCheckInResult(null);
    setShowCheckInDialog(true);
  };

  const openVotersDialog = async (box) => {
    setSelectedBox(box);
    setBoxVotersSearch('');
    setShowVotersDialog(true);
    setBoxVotersPage(1);
    setBoxVotersLoading(true);
    try {
      const params = { page: 1, page_size: boxVotersPageSize };
      const res = await api.get(`${API}/voting-boxes/${box.box_id}/constituents`, {
        params,
        withCredentials: true,
      });
      setBoxVoters(res.data.constituents || []);
      setBoxVotersTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Failed to load voters');
      setBoxVoters([]);
      setBoxVotersTotal(0);
    } finally {
      setBoxVotersLoading(false);
    }
  };

  const loadBoxVotersPage = async (page, search) => {
    if (!selectedBox) return;
    const searchParam = (search !== undefined ? search : boxVotersSearch).trim();
    setBoxVotersLoading(true);
    try {
      const params = { page, page_size: boxVotersPageSize };
      if (searchParam) params.search = searchParam;
      const res = await api.get(
        `${API}/voting-boxes/${selectedBox.box_id}/constituents`,
        { params, withCredentials: true }
      );
      setBoxVoters(res.data.constituents || []);
      setBoxVotersTotal(res.data.total || 0);
      setBoxVotersPage(page);
    } catch (e) {
      toast.error('Failed to load voters');
    } finally {
      setBoxVotersLoading(false);
    }
  };

  const handleBoxVotersSearchChange = (value) => {
    setBoxVotersSearch(value);
    if (!selectedBox) return;
    setBoxVotersPage(1);
    loadBoxVotersPage(1, value);
  };

  const openEditBoxDialog = (box) => {
    setSelectedBox(box);
    setEditBoxData({
      manual_registered: box.manual_registered ?? '',
      manual_voted: box.manual_voted ?? '',
      manual_pledges_registered: box.manual_pledges_registered ?? '',
      manual_pledgers_voted: box.manual_pledgers_voted ?? '',
    });
    setShowEditDialog(true);
  };

  const handleUpdateBox = async () => {
    if (!selectedBox) return;

    try {
      const payload = {};
      if (editBoxData.manual_registered !== '') {
        payload.manual_registered = parseInt(editBoxData.manual_registered, 10);
      } else {
        payload.manual_registered = -1;
      }
      if (editBoxData.manual_voted !== '') {
        payload.manual_voted = parseInt(editBoxData.manual_voted, 10);
      } else {
        payload.manual_voted = -1;
      }
      if (editBoxData.manual_pledges_registered !== '') {
        payload.manual_pledges_registered = parseInt(editBoxData.manual_pledges_registered, 10);
      } else {
        payload.manual_pledges_registered = -1;
      }
      if (editBoxData.manual_pledgers_voted !== '') {
        payload.manual_pledgers_voted = parseInt(editBoxData.manual_pledgers_voted, 10);
      } else {
        payload.manual_pledgers_voted = -1;
      }

      await api.put(`${API}/voting-boxes/${selectedBox.box_id}`, payload);
      toast.success('Voting box counts updated');
      setShowEditDialog(false);
      invalidateVoting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update box');
    }
  };

  const handleResetBoxCounts = async () => {
    if (!selectedBox) return;
    if (!window.confirm('Reset to auto-calculated values? This will clear all manual overrides.')) return;

    try {
      await api.post(`${API}/voting-boxes/${selectedBox.box_id}/reset-manual`, {});
      toast.success('Box counts reset to auto-calculated values');
      setShowEditDialog(false);
      invalidateVoting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset counts');
    }
  };

  const allBoxes = useMemo(() => {
    const list = [];
    Object.entries(boxesByCampaign).forEach(([campaignId, boxes]) => {
      boxes.forEach((box) => {
        list.push({ ...box, _campaignId: campaignId, _campaignName: campaignNames[campaignId] || campaignId });
      });
    });
    return list;
  }, [boxesByCampaign, campaignNames]);

  return (
    <Layout>
      <div className="p-4 md:p-8" data-testid="voting-boxes-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Voting Boxes
            </h1>
            <p className="text-muted-foreground">Manage your assigned voting boxes</p>
          </div>
          <Button variant="outline" onClick={invalidateVoting} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : allBoxes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Vote className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No voting boxes assigned</h3>
              <p className="text-muted-foreground">
                Contact your administrator to get assigned to voting boxes.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(boxesByCampaign).map(([campaignId, boxes]) => (
              <div key={campaignId}>
                <h2 className="text-xl font-semibold mb-4">{campaignNames[campaignId] || campaignId}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {boxes.map((box) => (
                    <Card key={box.box_id} className="hover:shadow-lg transition-shadow" data-testid="voting-box-card">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-lg">
                            {box.name}
                            {box.manual_registered !== undefined &&
                              box.manual_registered !== null && (
                                <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1 rounded">
                                  Manual
                                </span>
                              )}
                          </CardTitle>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openVotersDialog(box)}
                              title="View Voters"
                            >
                              <List className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditBoxDialog(box)}
                              title="Edit Counts"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        {box.description && (
                          <p className="text-sm text-muted-foreground">{box.description}</p>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm text-blue-700">Voted / Registered</span>
                              <span className="font-bold text-blue-900">
                                {(box.voted ?? 0).toLocaleString()} / {(box.registered ?? 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 transition-all duration-500"
                                style={{
                                  width: `${(box.registered ?? 0) > 0 ? ((box.voted ?? 0) / (box.registered ?? 0)) * 100 : 0}%`,
                                }}
                              />
                            </div>
                            <p className="text-xs text-center text-blue-600 mt-1">
                              {(box.registered ?? 0) > 0
                                ? (((box.voted ?? 0) / (box.registered ?? 0)) * 100).toFixed(1)
                                : 0}
                              % turnout
                            </p>
                          </div>

                          <div className="p-3 bg-emerald-50 rounded-lg">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm text-emerald-700">Pledgers Voted / Pledges</span>
                              <span className="font-bold text-emerald-900">
                                {(box.pledgers_voted ?? 0).toLocaleString()} /{' '}
                                {(box.pledges_registered ?? 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="h-2 bg-emerald-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-500 transition-all duration-500"
                                style={{
                                  width: `${
                                    (box.pledges_registered ?? 0) > 0
                                      ? ((box.pledgers_voted ?? 0) / (box.pledges_registered ?? 0)) * 100
                                      : 0
                                  }%`,
                                }}
                              />
                            </div>
                            <p className="text-xs text-center text-emerald-600 mt-1">
                              {(box.pledges_registered ?? 0) > 0
                                ? (((box.pledgers_voted ?? 0) / (box.pledges_registered ?? 0)) * 100).toFixed(1)
                                : 0}
                              % pledge turnout
                            </p>
                          </div>

                          <Button className="w-full mt-2" onClick={() => openCheckInDialog(box)} data-testid="check-in-btn">
                            <UserCheck className="h-4 w-4 mr-2" />
                            Check In Voter
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Check-in Dialog */}
        <Dialog open={showCheckInDialog} onOpenChange={setShowCheckInDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                Voter Check-in - {selectedBox?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>ID Card Number</Label>
                <Input
                  value={checkInId}
                  onChange={(e) => setCheckInId(e.target.value.toUpperCase())}
                  placeholder="A######"
                  className="text-lg font-mono"
                  onKeyDown={(e) => e.key === 'Enter' && handleCheckIn()}
                  autoFocus
                  data-testid="check-in-id-input"
                />
              </div>
              <Button
                className="w-full h-11 text-base"
                onClick={handleCheckIn}
                disabled={checkInLoading}
                data-testid="submit-check-in"
              >
                {checkInLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <UserCheck className="h-5 w-5 mr-2" />
                    Mark as Voted
                  </>
                )}
              </Button>
              {checkInResult && (
                <div
                  className={`p-4 rounded-lg ${
                    checkInResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}
                >
                  {checkInResult.success ? (
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-800">{checkInResult.message}</p>
                        {checkInResult.constituent && (
                          <div className="mt-2 text-sm text-green-700">
                            <p>
                              <strong>Name:</strong> {checkInResult.constituent.name}
                            </p>
                            <p>
                              <strong>ID:</strong> {checkInResult.constituent.id_number}
                            </p>
                            <p>
                              <strong>Block:</strong> {checkInResult.constituent.block_number}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="font-medium text-red-800">{checkInResult.message}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* View Voters Dialog */}
        <Dialog open={showVotersDialog} onOpenChange={setShowVotersDialog}>
          <DialogContent className="max-w-2xl h-[85vh] min-h-[500px] top-[5%] translate-y-0 flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Voters - {selectedBox?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              <div className="mt-2 ml-2 mb-3 flex items-center gap-2">
                <Input
                  placeholder="Search by ID"
                  value={boxVotersSearch}
                  onChange={(e) => handleBoxVotersSearchChange(e.target.value)}
                  className="max-w-xs font-mono"
                />
                {boxVotersLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent flex-shrink-0" />
                )}
              </div>
              {boxVotersLoading && boxVoters.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
                </div>
              ) : (
                <>
                  <div className="overflow-auto flex-1 min-h-0 border rounded-md">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">Name</th>
                          <th className="text-left p-2 font-medium">ID</th>
                          <th className="text-left p-2 font-medium">Address</th>
                          <th className="text-center p-2 font-medium w-12">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {boxVoters.map((v) => (
                          <tr key={v.record_id} className="border-b last:border-b-0 hover:bg-muted/50">
                            <td className="p-2">{v.name}</td>
                            <td className="p-2 font-mono">{v.id_number}</td>
                            <td className="p-2">{v.address || '—'}</td>
                            <td className="p-2 text-center align-middle">
                              {v.voted ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600 inline-block" title="Voted" />
                              ) : (
                                <CheckCircle2 className="h-4 w-4 text-muted-foreground opacity-40 inline-block" title="Not voted" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t mt-3">
                    <p className="text-sm text-muted-foreground">
                      {boxVotersTotal} voter{boxVotersTotal !== 1 ? 's' : ''} total
                    </p>
                    {boxVotersTotal > boxVotersPageSize && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={boxVotersPage <= 1}
                          onClick={() => loadBoxVotersPage(boxVotersPage - 1)}
                        >
                          Previous
                        </Button>
                        <span className="text-sm py-2 px-1">
                          Page {boxVotersPage} of {Math.ceil(boxVotersTotal / boxVotersPageSize)}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={boxVotersPage >= Math.ceil(boxVotersTotal / boxVotersPageSize)}
                          onClick={() => loadBoxVotersPage(boxVotersPage + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Box Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="h-5 w-5" />
                Edit Counts - {selectedBox?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Override auto-calculated counts with manual values. Leave empty to use auto-calculated values.
              </p>
              {selectedBox && (
                <div className="p-3 bg-muted rounded-lg text-sm">
                  <p className="font-medium mb-2">Auto-calculated values:</p>
                  <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                    <span>Registered: {selectedBox.calculated_registered ?? '—'}</span>
                    <span>Voted: {selectedBox.calculated_voted ?? '—'}</span>
                    <span>Pledges: {selectedBox.calculated_pledges_registered ?? '—'}</span>
                    <span>Pledgers Voted: {selectedBox.calculated_pledgers_voted ?? '—'}</span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Manual Registered</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editBoxData.manual_registered}
                    onChange={(e) => setEditBoxData({ ...editBoxData, manual_registered: e.target.value })}
                    placeholder="Auto"
                  />
                </div>
                <div>
                  <Label>Manual Voted</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editBoxData.manual_voted}
                    onChange={(e) => setEditBoxData({ ...editBoxData, manual_voted: e.target.value })}
                    placeholder="Auto"
                  />
                </div>
                <div>
                  <Label>Pledges Registered</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editBoxData.manual_pledges_registered}
                    onChange={(e) =>
                      setEditBoxData({ ...editBoxData, manual_pledges_registered: e.target.value })
                    }
                    placeholder="Auto"
                  />
                </div>
                <div>
                  <Label>Pledgers Voted</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editBoxData.manual_pledgers_voted}
                    onChange={(e) => setEditBoxData({ ...editBoxData, manual_pledgers_voted: e.target.value })}
                    placeholder="Auto"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleUpdateBox} className="flex-1">
                  Save Changes
                </Button>
                <Button variant="outline" onClick={handleResetBoxCounts} title="Reset to auto-calculated values">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
